import type { QueryKey } from '@tanstack/react-query';
import type { LiveEvent } from '../api/events';
import { accountKeys } from '../hooks/useAccounts';
import { billingKeys } from '../hooks/useBilling';
import { contentKeys, contentItemKeys } from '../hooks/useContent';
import { contentTypeKeys } from '../hooks/useContentTypes';
import { siteKeys } from '../hooks/useSites';
import { barkKeys } from '../hooks/useBark';
import { formKeys } from '../hooks/useForms';
import { memberKeys, tierKeys } from '../hooks/useMembers';
import { themeKeys, accountThemeKeys } from '../hooks/useTheme';
import { tagKeys } from '../hooks/useTags';
import { snapshotKeys } from '../hooks/useSnapshots';
import { connectorKeys } from '../hooks/useConnectors';
import { languagesKeys } from '../api/languages';
import { staticFileKeys } from '../hooks/useStaticFiles';

/** The same cache scope applies to completion readback, mutations and remote events. */
export function restoredInputKeys(siteSlug: string): QueryKey[] {
  return eventQueryKeys({ domain: 'site', action: 'restored', site_slug: siteSlug });
}

/** Explicit event domains only. Never turn an unknown event into a blanket site refetch. */
export function eventQueryKeys(event: Pick<LiveEvent, 'domain' | 'action' | 'site_slug' | 'content_type' | 'account_pid' | 'row_id'>): QueryKey[] {
  const site = event.site_slug;
  if (event.domain === 'billing') return [billingKeys.status()];
  if (event.domain === 'account') return [accountKeys.list(), siteKeys.list(), ...(event.account_pid ? [accountKeys.members(event.account_pid), accountKeys.invitations(event.account_pid)] : [])];
  if (!site) return [];
  // AB#820: the deploy indicator answers "what changed since the last publish?",
  // so every domain that can dirty a build rides along with the site's own rows.
  const siteRows = [siteKeys.detail(site), siteKeys.settings(site), siteKeys.list(), siteKeys.deployState(site)];
  switch (event.domain) {
    case 'posts': return [contentKeys.posts.all(site), tagKeys.all(site), siteKeys.deployState(site)];
    case 'pages': return [contentKeys.pages.all(site), siteKeys.deployState(site)];
    case 'media': return [contentKeys.media.all(site)];
    case 'content-types': return [contentTypeKeys.all(site), ...(event.content_type ? [contentItemKeys.all(site, event.content_type)] : [])];
    case 'forms': return [formKeys.all(site)];
    case 'bark': return [barkKeys.site(site)];
    case 'members': return [[...memberKeys.lists(), site], [...memberKeys.details(), site], memberKeys.stats(site), tierKeys.list(site), [...tierKeys.details(), site]];
    case 'theme': return [themeKeys.all(site), accountThemeKeys.all, siteKeys.deployState(site)];
    case 'settings': return siteRows;
    case 'migration': return [siteKeys.migrationStatus(site), ...siteRows];
    case 'platforms': return [barkKeys.platformConnectionsForSite(site), connectorKeys.all];
    case 'site': return event.action === 'restored' ? [
      ...siteRows, snapshotKeys.site(site), contentKeys.posts.all(site), contentKeys.pages.all(site), contentKeys.media.all(site),
      contentTypeKeys.all(site), contentTypeKeys.schema(site), ['sites', site, 'content'],
      tagKeys.all(site), ['sites', site, 'post-tags'], formKeys.all(site), languagesKeys.detail(site), staticFileKeys.all(site),
      themeKeys.all(site), accountThemeKeys.all, barkKeys.site(site),
    ] : event.action === 'retention_changed' ? [...siteRows, snapshotKeys.site(site), contentKeys.media.all(site)]
      : event.action.startsWith('deploy_') ? [...siteRows, ['cloudflare', 'deployments', { siteSlug: site }], ['cloudflare', 'domain-status', { siteSlug: site }]]
      : event.action.startsWith('build_') ? [...siteRows, themeKeys.all(site), snapshotKeys.site(site), contentKeys.media.all(site)] : siteRows;
    default: return [];
  }
}
